/**
 * VersionInfo — renders the running build's version, build number,
 * commit SHA, deploy timestamp, and auto-generated change list for both
 * the frontend bundle and the backend image.
 *
 * Frontend version/build/commit/changes are baked into the bundle via
 * Vite's `define` block (see vite.config.ts) and read here as
 * compile-time constants — no network call needed for those.
 * `deployedAt` is the exception: it lives on the environment (written
 * to .env.deploy at SSM rollout time), so both the Frontend and Server
 * lines pick it up from the server's /api/build-info response — the
 * server is the source of truth for "when did this environment last
 * receive a deploy."
 *
 * Two lines are intentional: when a deploy is half-applied (new
 * server image, stale frontend dist) the divergence is self-
 * documenting. ottotester#187 locked this layout; #188 added
 * deployedAt as the per-environment signal; #677 added the build
 * number and dropped the redundant "Built" timestamp row — `builtAt`
 * and `deployedAt` are always seconds apart (the pipeline rebuilds on
 * every deploy), so `Deployed` carries the per-environment "since
 * when" and the SHA + build number already pin the artifact. The
 * `builtAt` field still rides along in /api/build-info; only the panel
 * row is gone. #683 added the expandable change-list section below the
 * version lines — the server's change list is the one shown (the
 * server is the source of truth for what shipped to this environment).
 */
import { useEffect, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'

/** The auto-generated change list (ottotester#683 slice 2). */
type ChangeList = {
  /** Last release tag the list is diffed against — empty when no tag
   * is reachable (e.g. a `.git`-less source tree). */
  since: string
  groups: { new: string[]; fixed: string[]; other: string[] }
  /** Count of entries beyond the display cap — rendered as "and N more". */
  overflow: number
}

type BuildInfo = {
  version: string
  /** Total commit count of the built ref — monotonic and comparable
   * across localhost / dev / prod (ottotester#677). */
  build: number
  commit: string
  builtAt: string
  deployedAt: string
  /** Auto-generated change list since the last release (#683). */
  changes: ChangeList
}

const EMPTY_CHANGES: ChangeList = {
  since: '',
  groups: { new: [], fixed: [], other: [] },
  overflow: 0,
}

/** Parse the bundled __BUILD_CHANGES__ constant — a JSON string Vite
 * bakes in (see vite.config.ts). Guarded: a malformed value yields an
 * empty change list rather than crashing the panel. */
function parseBundledChanges(): ChangeList {
  try {
    const parsed = JSON.parse(__BUILD_CHANGES__) as unknown
    return normalizeChanges(parsed)
  } catch {
    return EMPTY_CHANGES
  }
}

/** Narrow an unknown value to a ChangeList, filling defaults for any
 * missing / malformed field. Used for both the bundled constant and the
 * server fetch response. */
function normalizeChanges(value: unknown): ChangeList {
  if (!value || typeof value !== 'object') return EMPTY_CHANGES
  const v = value as Record<string, unknown>
  const g = (v.groups && typeof v.groups === 'object' ? v.groups : {}) as Record<
    string,
    unknown
  >
  const arr = (x: unknown): string[] =>
    Array.isArray(x) ? x.filter((s): s is string => typeof s === 'string') : []
  return {
    since: typeof v.since === 'string' ? v.since : '',
    groups: { new: arr(g.new), fixed: arr(g.fixed), other: arr(g.other) },
    overflow:
      typeof v.overflow === 'number' && Number.isInteger(v.overflow) && v.overflow >= 0
        ? v.overflow
        : 0,
  }
}

const FRONTEND_BUILD: Omit<BuildInfo, 'deployedAt'> = {
  version: __BUILD_VERSION__,
  // Non-negative integer only — the value is a commit count. The
  // digits-only test rejects trailing garbage and negatives that a
  // bare `parseInt` would silently coerce. (vite.config already
  // normalizes it; this is defense-in-depth at the consuming end.)
  build: /^\d+$/.test(__BUILD_NUMBER__) ? Number(__BUILD_NUMBER__) : 0,
  commit: __BUILD_COMMIT__,
  builtAt: __BUILD_TIMESTAMP__,
  changes: parseBundledChanges(),
}

const FALLBACK_SERVER_BUILD: BuildInfo = {
  version: 'dev',
  build: 0,
  commit: 'local',
  builtAt: '',
  deployedAt: '',
  changes: EMPTY_CHANGES,
}

/** Total entry count across all three groups — drives the collapsed
 * summary line ("7 changes since v0.4.0"). */
function changeCount(c: ChangeList): number {
  return c.groups.new.length + c.groups.fixed.length + c.groups.other.length
}

function formatTimestamp(iso: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  // YYYY-MM-DD HH:MM UTC — short, sortable, deterministic. Matches the
  // example shown in ottotester#187.
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`
  )
}

function Line({ label, info }: { label: string; info: BuildInfo }) {
  return (
    <div className="font-mono text-xs text-muted-foreground">
      <span className="font-sans text-foreground">{label}</span>{' '}
      {info.version} <span aria-hidden="true">·</span> build {info.build}{' '}
      <span aria-hidden="true">·</span> {info.commit}{' '}
      <span aria-hidden="true">·</span> deployed{' '}
      {formatTimestamp(info.deployedAt)}
    </div>
  )
}

/** One labelled bucket of change entries (New / Fixed / Other). Renders
 * nothing when the bucket is empty. */
function ChangeGroup({ label, entries }: { label: string; entries: string[] }) {
  if (entries.length === 0) return null
  return (
    <div className="space-y-0.5">
      <div className="text-xs font-medium text-foreground">{label}</div>
      <ul className="space-y-0.5">
        {entries.map((entry, i) => (
          <li
            key={`${label}-${i}`}
            className="font-mono text-xs text-muted-foreground"
          >
            {entry}
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Expandable change-list section — the auto-generated release notes
 * (ottotester#683 slice 2). Collapsed: a one-line summary ("7 changes
 * since v0.4.0"). Expanded: the New / Fixed / Other buckets, plus an
 * "and N more" line when entries were capped. When there are no
 * changes (HEAD on the release tag, or a `.git`-less build) it renders
 * a quiet "Up to date" line instead — no toggle.
 */
function Changes({ changes }: { changes: ChangeList }) {
  const [expanded, setExpanded] = useState(false)
  const total = changeCount(changes)

  if (total === 0) {
    return (
      <div className="text-xs text-muted-foreground">
        Up to date{changes.since ? ` with ${changes.since}` : ''}
      </div>
    )
  }

  const sinceLabel = changes.since ? ` since ${changes.since}` : ''
  const summary = `${total} change${total === 1 ? '' : 's'}${sinceLabel}`

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        className="flex items-center gap-1 text-xs text-foreground hover:underline"
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-3 w-3" aria-hidden="true" />
        )}
        {summary}
      </button>
      {expanded && (
        <div className="space-y-2 pl-4">
          <ChangeGroup label="New" entries={changes.groups.new} />
          <ChangeGroup label="Fixed" entries={changes.groups.fixed} />
          <ChangeGroup label="Other" entries={changes.groups.other} />
          {changes.overflow > 0 && (
            <div className="text-xs text-muted-foreground">
              and {changes.overflow} more
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export type VersionInfoProps = {
  /** Pre-resolved frontend metadata. Tests pass this in directly to
   * exercise the rendering without depending on Vite's define. In
   * production it defaults to the bundled constants for
   * version/build/commit/builtAt; deployedAt comes from the server
   * fetch (or the prop, if supplied). */
  frontend?: Partial<BuildInfo>
  /** Pre-resolved server metadata. When omitted, the component
   * fetches /api/build-info and renders fallback defaults until the
   * response lands (or on fetch failure). */
  server?: BuildInfo
  /** Override the server fetch URL. Defaults to /api/build-info. Used
   * by tests to point at a mock. */
  serverEndpoint?: string
}

export function VersionInfo({
  frontend,
  server,
  serverEndpoint = '/api/build-info',
}: VersionInfoProps = {}) {
  const [serverInfo, setServerInfo] = useState<BuildInfo>(
    server ?? FALLBACK_SERVER_BUILD,
  )

  useEffect(() => {
    if (server) return
    let cancelled = false
    fetch(serverEndpoint, { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) return
        const data = (await res.json()) as Partial<BuildInfo>
        if (cancelled) return
        setServerInfo({
          version: data.version || FALLBACK_SERVER_BUILD.version,
          build:
            typeof data.build === 'number'
              ? data.build
              : FALLBACK_SERVER_BUILD.build,
          commit: data.commit || FALLBACK_SERVER_BUILD.commit,
          builtAt: data.builtAt || FALLBACK_SERVER_BUILD.builtAt,
          deployedAt: data.deployedAt || FALLBACK_SERVER_BUILD.deployedAt,
          // Guarded — a missing / malformed `changes` field yields an
          // empty change list rather than crashing the panel.
          changes: normalizeChanges(data.changes),
        })
      })
      .catch(() => {
        // Leave fallback in place. Component never blanks.
      })
    return () => {
      cancelled = true
    }
  }, [server, serverEndpoint])

  // Frontend's deployedAt mirrors the server's — they deployed
  // together, the server is the source of truth. If the caller
  // passed an explicit `frontend.deployedAt`, that wins (tests).
  const frontendInfo: BuildInfo = {
    version: frontend?.version ?? FRONTEND_BUILD.version,
    build: frontend?.build ?? FRONTEND_BUILD.build,
    commit: frontend?.commit ?? FRONTEND_BUILD.commit,
    builtAt: frontend?.builtAt ?? FRONTEND_BUILD.builtAt,
    deployedAt: frontend?.deployedAt ?? serverInfo.deployedAt,
    changes: frontend?.changes ?? FRONTEND_BUILD.changes,
  }

  // The change list shown is the server's — the server is the source
  // of truth for what shipped to this environment. (Frontend and
  // server change lists agree in a clean deploy; on a half-applied
  // deploy the version lines above already surface the divergence.)
  return (
    <section aria-label="Build version" className="space-y-2">
      <div className="space-y-1">
        <Line label="Frontend" info={frontendInfo} />
        <Line label="Server" info={serverInfo} />
      </div>
      <Changes changes={serverInfo.changes} />
    </section>
  )
}
